import type {DefinitionOrReference} from '@github-ui/code-nav'
import {ObservableMap, ObservableValue} from '@github-ui/observable'
import {useDerivedObservable, useObservableMap, useObservableValue, useObservedState} from '@github-ui/react-observable'
import type React from 'react'
import {createContext, use, useEffect, useMemo} from 'react'

// Three separate contexts so each consumer subscribes to exactly what it needs:
//
// - `ResultsByLineNumberContext` / `FocusedResultContext` hold observables whose references
//   are stable for the provider's lifetime. Consumers re-render via observable subscription
//   (`useObservedState`), never from context, so these context values never change.
// - `AllResultsByLineNumberContext` holds a plain Map that changes reference on every results
//   change (see `SearchResultsProvider`). Keeping it separate means only its single consumer
//   (the symbol-highlight overlay) re-renders when results change, not the per-line consumers.
const ResultsByLineNumberContext = createContext<ObservableMap<number, DefinitionOrReference[]>>(new ObservableMap())

const FocusedResultContext = createContext<ObservableValue<DefinitionOrReference | undefined>>(
  new ObservableValue<DefinitionOrReference | undefined>(undefined),
)

const AllResultsByLineNumberContext = createContext<Map<number, DefinitionOrReference[]>>(new Map())

export function SearchResultsProvider({
  searchResults,
  focusedSearchResult,
  children,
}: React.PropsWithChildren<{
  searchResults: DefinitionOrReference[]
  focusedSearchResult: number | undefined
}>) {
  const resultsByLineNumber = useObservableMap<number, DefinitionOrReference[]>()
  const focusedResult = useObservableValue(
    focusedSearchResult !== undefined ? searchResults[focusedSearchResult] : undefined,
  )

  // Group the flat results by line once, derived directly from the prop. Because this is a
  // plain memo it produces a fresh Map reference whenever `searchResults` changes, which is
  // what the all-results consumer (`useAllSearchResults`) needs: the symbol-highlight overlay
  // maps over it, and that JSX is memoized by the React Compiler (which compiles this package
  // on the dotcom build, but not the UI Service vite build). A reference-stable Map — as the
  // in-place-mutated `ObservableMap` would hand back — would leave that memo stale and the
  // overlay empty.
  const allResultsByLineNumber = useMemo(() => {
    const map = new Map<number, DefinitionOrReference[]>()
    for (const searchResult of searchResults) {
      const lineNumber = searchResult.lineNumber
      const existing = map.get(lineNumber)
      if (existing) {
        existing.push(searchResult)
      } else {
        map.set(lineNumber, [searchResult])
      }
    }
    return map
  }, [searchResults])

  useEffect(() => {
    // Sync the observable map (consumed per-line by `useSearchResults` so individual lines
    // re-render independently) from the grouped map.
    resultsByLineNumber.clear()
    for (const [lineNumber, resultsForLine] of allResultsByLineNumber) {
      resultsByLineNumber.set(lineNumber, resultsForLine)
    }
  }, [resultsByLineNumber, allResultsByLineNumber])

  useEffect(() => {
    focusedResult.setValue(focusedSearchResult !== undefined ? searchResults[focusedSearchResult] : undefined)
  }, [searchResults, focusedResult, focusedSearchResult])

  return (
    <ResultsByLineNumberContext value={resultsByLineNumber}>
      <FocusedResultContext value={focusedResult}>
        <AllResultsByLineNumberContext value={allResultsByLineNumber}>{children}</AllResultsByLineNumberContext>
      </FocusedResultContext>
    </ResultsByLineNumberContext>
  )
}

export function useAllSearchResults(): Map<number, DefinitionOrReference[]> {
  // A plain, memoized Map derived from the `searchResults` prop — its reference changes on
  // every results change, so a compiler-memoized consumer (the symbol-highlight overlay)
  // recomputes. See the note in `SearchResultsProvider` for why a fresh reference matters.
  return use(AllResultsByLineNumberContext)
}

export function useSearchResults(lineNumber: number): DefinitionOrReference[] | undefined {
  const resultsByLineNumber = use(ResultsByLineNumberContext)
  return useObservedState(resultsByLineNumber.get(lineNumber))
}

export function useFocusedSearchResult(lineNumber: number): DefinitionOrReference | undefined {
  const focusedResult = use(FocusedResultContext)
  const focusedResultForLine = useDerivedObservable(focusedResult, result =>
    result?.lineNumber === lineNumber ? result : undefined,
  )
  return useObservedState(focusedResultForLine)
}

export function useFocusedSearchResultWithoutLineCheck(): DefinitionOrReference | undefined {
  const focusedResult = use(FocusedResultContext)
  return useObservedState(focusedResult)
}
